import {
  Box3,
  BoxGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  type Object3D,
  Plane,
  Vector3
} from "three";
import type { DxfPalletRackCellPlacement } from "../cad/pallet-rack-layout";
import { type ModelAssetLoader, modelAssetLibrary } from "../models/model-asset-library";
import { disposeObject3DResources as disposeMeshes } from "../models/shared-model-resources";
import {
  convertLegacyLayerSpansToClearHeights,
  createRackLayerMetrics,
  type RackLayerHeightMode
} from "./rack-layer-metrics";
import { RackPost, RackPostDirection } from "./rack-post";
import { measureRackCrossbarHeight, RackPostColumn } from "./rack-post-column";
import {
  createCargoCellHighlight,
  createCargoCellSelectionHighlight,
  createRackSelectionHighlight
} from "./rack-shelf";
import { RackShelfSideBrace } from "./rack-shelf-side-brace";

export type PalletRackCargoCell = {
  cellId: string;
  cellIndex: number;
  heightIndex: number;
};

export type PalletRackCargoPlacement = PalletRackCargoCell & {
  slotIndex: number;
};

export type PalletRackCellConfiguration = {
  cargoCapacity: number;
  cargoRowCount: number;
  length: number;
  width: number;
};

export type PalletRackConfiguration = {
  layerHeights: number[];
};

type PalletRackPosition = {
  x?: number;
  y?: number;
  z?: number;
};

export type PalletRackOptions = {
  cells: DxfPalletRackCellPlacement[];
  layerHeightMode?: RackLayerHeightMode;
  layerHeights?: number[];
  position?: PalletRackPosition;
  rotationY?: number;
};

type PalletRackCargo = PalletRackCargoPlacement & {
  root: Group;
};

type PalletRackCellFrame = {
  backZ: number;
  cell: DxfPalletRackCellPlacement;
  frontZ: number;
  leftX: number;
  rightX: number;
};

type PalletRackHitTarget = Mesh<BoxGeometry, MeshBasicMaterial> & {
  userData: {
    palletRack: PalletRack;
    palletRackCargoCell: PalletRackCargoCell;
  };
};

type PalletRackSideFrame = {
  backZ: number;
  frontZ: number;
  x: number;
};

const defaultLayerHeight = 1.3;
const defaultLayerCount = 3;
const bottomBraceY = 0.5;
const topBraceInset = 0.5;
const columnBraceXOffset = 0.026;
const crossbarZOffset = 0.0185;
const columnBraceLengthScale = 4;
const columnClipAreaXHalfLength = 1;
const diagonalBraceMaxAngle = Math.PI / 3;
const diagonalBraceMinAngle = (36 * Math.PI) / 180;
const cargoCrossbarTopOffset = 0.05;
const rackSelectionHighlightPadding = 0.06;
const layoutTolerance = 1e-5;

export const defaultPalletRackLayerHeights = Array.from(
  { length: defaultLayerCount },
  () => defaultLayerHeight
);

export class PalletRack extends Group {
  private readonly cargoCellGroup = new Group();
  private readonly cargoCellHighlight = createCargoCellHighlight();
  private readonly cargoCellSelectionHighlight = createCargoCellSelectionHighlight();
  private readonly cargoGroup = new Group();
  private readonly cargoPlacements = new Map<string, PalletRackCargo>();
  private cellFrames = new Map<string, PalletRackCellFrame>();
  private readonly cells: DxfPalletRackCellPlacement[];
  private crossbarHeight = 0;
  private hitTargets: PalletRackHitTarget[] = [];
  private layerHeightMode: RackLayerHeightMode;
  private layerHeights: number[];
  private readonly rackSelectionHighlight = createRackSelectionHighlight();
  private selected = false;
  private structureGroup = new Group();
  private updateQueue: Promise<void> = Promise.resolve();

  constructor({
    cells,
    layerHeightMode = "clear",
    layerHeights = defaultPalletRackLayerHeights,
    position = {},
    rotationY = 0
  }: PalletRackOptions) {
    super();
    this.cells = validateCells(cells).map((cell) => ({ ...cell }));
    this.layerHeightMode = layerHeightMode;
    this.layerHeights = validateLayerHeights(layerHeights);
    this.name = "pallet-rack";
    this.structureGroup.name = "pallet-rack-structure";
    this.cargoGroup.name = "pallet-rack-cargo";
    this.cargoCellGroup.name = "pallet-rack-cargo-cells";
    this.cargoCellGroup.add(this.cargoCellSelectionHighlight, this.cargoCellHighlight);
    this.add(
      this.structureGroup,
      this.cargoGroup,
      this.cargoCellGroup,
      this.rackSelectionHighlight
    );
    this.position.set(position.x ?? 0, position.y ?? 0, position.z ?? 0);
    this.rotation.y = rotationY;
  }

  static async create(options: PalletRackOptions, loader: ModelAssetLoader = modelAssetLibrary) {
    const rack = new PalletRack(options);
    await rack.rebuildStructure(loader);
    return rack;
  }

  getCargoCellHitTargets() {
    return this.hitTargets;
  }

  getCargoCell(hitTarget: Object3D): PalletRackCargoCell | undefined {
    if (!this.isHitTarget(hitTarget)) {
      return;
    }
    return { ...hitTarget.userData.palletRackCargoCell };
  }

  getCargoCellHitTarget(cell: PalletRackCargoCell) {
    return this.hitTargets.find((target) => {
      const targetCell = target.userData.palletRackCargoCell;
      return targetCell.cellId === cell.cellId && targetCell.heightIndex === cell.heightIndex;
    });
  }

  getCellConfiguration(cellId: string): PalletRackCellConfiguration {
    const cell = this.getCell(cellId);
    return {
      cargoCapacity: cell.cargoCapacity,
      cargoRowCount: cell.cargoRowCount,
      length: cell.length,
      width: cell.width
    };
  }

  getCellCount() {
    return this.cells.length;
  }

  getConfiguration(): PalletRackConfiguration {
    return { layerHeights: [...this.layerHeights] };
  }

  showCargoCellHighlight(hitTarget: Object3D) {
    if (!this.isHitTarget(hitTarget)) {
      return;
    }

    this.cargoCellHighlight.position.copy(hitTarget.position);
    this.cargoCellHighlight.scale.copy(hitTarget.scale);
    this.cargoCellHighlight.visible = true;
  }

  hideCargoCellHighlight() {
    this.cargoCellHighlight.visible = false;
  }

  selectCargoCell(hitTarget: Object3D) {
    if (!this.isHitTarget(hitTarget)) {
      return;
    }

    this.cargoCellSelectionHighlight.position.copy(hitTarget.position);
    this.cargoCellSelectionHighlight.scale.copy(hitTarget.scale).multiplyScalar(0.94);
    this.cargoCellSelectionHighlight.visible = true;
  }

  clearCargoCellSelection() {
    this.cargoCellSelectionHighlight.visible = false;
  }

  setSelected(selected: boolean) {
    this.selected = selected;
    this.rackSelectionHighlight.visible = selected;
    if (selected) {
      this.updateRackSelectionHighlight();
    } else {
      this.clearCargoCellSelection();
    }
  }

  setCargo(placement: PalletRackCargoPlacement, object: Object3D) {
    const validPlacement = this.validateCargoPlacement(placement);
    const key = createCargoPlacementKey(validPlacement);
    this.removeCargo(validPlacement);

    const root = createCargoRoot(object);
    const cargo = { ...validPlacement, root };
    this.cargoPlacements.set(key, cargo);
    this.cargoGroup.add(root);
    this.updateCargoPlacement(cargo);
  }

  removeCargo(placement: PalletRackCargoPlacement) {
    const validPlacement = this.validateCargoPlacement(placement);
    const key = createCargoPlacementKey(validPlacement);
    const cargo = this.cargoPlacements.get(key);
    if (!cargo) {
      return;
    }

    this.cargoGroup.remove(cargo.root);
    this.cargoPlacements.delete(key);
    disposeMeshes(cargo.root);
  }

  setCargoCapacity(cellId: string, cargoCapacity: number) {
    const cell = this.getCell(cellId);
    const nextCargoCapacity = validatePositiveInteger(cargoCapacity, "cargoCapacity");
    if (cell.cargoCapacity === nextCargoCapacity) {
      return;
    }
    cell.cargoCapacity = nextCargoCapacity;
    this.pruneCargoPlacements();
    this.updateCargoPlacements();
  }

  setLayerHeights(layerHeights: number[], loader: ModelAssetLoader = modelAssetLibrary) {
    const nextLayerHeights = validateLayerHeights(layerHeights);
    if (areEqualNumbers(this.layerHeights, nextLayerHeights)) {
      return Promise.resolve();
    }
    return this.enqueueUpdate(async () => {
      this.layerHeightMode = "clear";
      this.layerHeights = nextLayerHeights;
      this.pruneCargoPlacements();
      await this.rebuildStructure(loader);
      this.updateCargoPlacements();
    });
  }

  private enqueueUpdate(update: () => Promise<void>) {
    const nextUpdate = this.updateQueue.then(update);
    this.updateQueue = nextUpdate.catch(() => undefined);
    return nextUpdate;
  }

  private async rebuildStructure(loader: ModelAssetLoader) {
    const previousStructure = this.structureGroup;
    const structure = new Group();
    structure.name = "pallet-rack-structure";

    this.crossbarHeight = await measureRackCrossbarHeight(loader);
    if (this.layerHeightMode === "legacy-span") {
      this.layerHeights = validateLayerHeights(
        convertLegacyLayerSpansToClearHeights(this.layerHeights, this.crossbarHeight)
      );
      this.layerHeightMode = "clear";
    }

    const probePost = await RackPost.create(
      {
        angleIronYPositions: [],
        height: this.getTotalHeight()
      },
      loader
    );
    const postWidth = probePost.getBodyWidth();
    const postDepth = probePost.getBodyDepth();
    const postBaseY = probePost.getPostBaseY();
    const crossbarYPositions = this.getInternalLayerYPositions().map((y) => y + postBaseY);
    await probePost.setAngleIronYPositions(crossbarYPositions, loader);
    this.cellFrames = createCellFrames(this.cells, postWidth, postDepth);
    const postPositions = createPostPositions(this.cellFrames);
    const firstPostPosition = postPositions.shift();

    if (firstPostPosition) {
      probePost.setDirection(firstPostPosition.direction);
      probePost.setRackPosition(firstPostPosition.position);
      structure.add(probePost);
    } else {
      disposeMeshes(probePost);
    }

    const posts = await Promise.all(
      postPositions.map(({ direction, position }) =>
        RackPost.create(
          {
            angleIronYPositions: crossbarYPositions,
            direction,
            height: this.getTotalHeight(),
            position
          },
          loader
        )
      )
    );
    structure.add(...posts);

    const postBodyHeight = probePost.getPostBodyHeight();
    const columns = await Promise.all(
      [...this.cellFrames.values()].flatMap((frame) =>
        [
          { direction: RackPostDirection.Front, z: frame.frontZ },
          { direction: RackPostDirection.Back, z: frame.backZ }
        ].map(({ direction, z }) =>
          RackPostColumn.create(
            {
              crossbarYPositions,
              crossbarZOffset,
              direction,
              position: { x: frame.leftX, z },
              postBaseY,
              postBodyHeight,
              postWidth,
              spanLength: frame.rightX - frame.leftX
            },
            loader
          )
        )
      )
    );
    structure.add(...columns);

    const sideBraces = await Promise.all(
      createSideFrames(this.cellFrames).map((frame, index) =>
        createSideBraceFrame(frame, index, this.getTotalHeight(), this, loader)
      )
    );
    structure.add(...sideBraces);

    previousStructure.removeFromParent();
    disposeMeshes(previousStructure);
    this.structureGroup = structure;
    this.add(structure);
    structure.updateWorldMatrix(true, true);
    updateSideBraceClipPlanes(structure, this);
    this.reloadHitTargets();
    this.updateRackSelectionHighlight();
  }

  private reloadHitTargets() {
    this.hideCargoCellHighlight();
    this.clearCargoCellSelection();
    this.hitTargets.forEach((target) => {
      this.cargoCellGroup.remove(target);
      disposeMeshes(target);
    });
    this.hitTargets = [];

    this.cells.forEach((cell, cellIndex) => {
      const frame = this.cellFrames.get(cell.id);
      if (!frame) {
        return;
      }

      this.layerHeights.forEach((height, heightIndex) => {
        const target = createHitTarget(this, { cellId: cell.id, cellIndex, heightIndex });
        const layerBottom = this.getLayerClearBottom(heightIndex);
        target.position.set(
          (frame.leftX + frame.rightX) / 2,
          layerBottom + height / 2,
          (frame.frontZ + frame.backZ) / 2
        );
        target.scale.set(frame.rightX - frame.leftX, height, frame.backZ - frame.frontZ);
        this.hitTargets.push(target);
      });
    });

    this.cargoCellGroup.add(...this.hitTargets);
  }

  private updateRackSelectionHighlight() {
    const bounds = getRackBounds(this.cells);
    const totalHeight = this.getTotalHeight();
    this.rackSelectionHighlight.position.set(
      (bounds.minX + bounds.maxX) / 2,
      totalHeight / 2,
      (bounds.minZ + bounds.maxZ) / 2
    );
    this.rackSelectionHighlight.scale.set(
      bounds.maxX - bounds.minX + rackSelectionHighlightPadding * 2,
      totalHeight + rackSelectionHighlightPadding * 2,
      bounds.maxZ - bounds.minZ + rackSelectionHighlightPadding * 2
    );
    this.rackSelectionHighlight.visible = this.selected;
  }

  private updateCargoPlacements() {
    this.cargoPlacements.forEach((cargo) => {
      this.updateCargoPlacement(cargo);
    });
  }

  private updateCargoPlacement(cargo: PalletRackCargo) {
    const frame = this.cellFrames.get(cargo.cellId);
    const cell = this.getCell(cargo.cellId);
    if (!frame) {
      return;
    }

    const slotCenter = (cargo.slotIndex + 0.5) / cell.cargoCapacity;
    const supportSurfaceOffset = cargo.heightIndex > 0 ? cargoCrossbarTopOffset : 0;
    cargo.root.position.set(
      frame.leftX + (frame.rightX - frame.leftX) * slotCenter,
      this.getCargoLayerBase(cargo.heightIndex) + supportSurfaceOffset,
      (frame.frontZ + frame.backZ) / 2
    );
  }

  private pruneCargoPlacements() {
    this.cargoPlacements.forEach((cargo, key) => {
      const cell = this.cells.find((item) => item.id === cargo.cellId);
      if (
        !cell ||
        cargo.heightIndex >= this.layerHeights.length ||
        cargo.slotIndex >= cell.cargoCapacity
      ) {
        this.cargoGroup.remove(cargo.root);
        this.cargoPlacements.delete(key);
        disposeMeshes(cargo.root);
      }
    });
  }

  private getLayerClearBottom(heightIndex: number) {
    return this.getLayerMetrics().layers[heightIndex]?.clearBottomY ?? 0;
  }

  private getCargoLayerBase(heightIndex: number) {
    return this.getLayerMetrics().layers[heightIndex]?.cargoBaseY ?? 0;
  }

  private getInternalLayerYPositions() {
    return this.getLayerMetrics().layers.flatMap(({ crossbarCenterY }) =>
      crossbarCenterY === undefined ? [] : [crossbarCenterY]
    );
  }

  private getTotalHeight() {
    return this.getLayerMetrics().totalHeight;
  }

  private getLayerMetrics() {
    return createRackLayerMetrics(this.layerHeights, this.crossbarHeight);
  }

  private getCell(cellId: string) {
    const cell = this.cells.find((item) => item.id === cellId);
    if (!cell) {
      throw new RangeError(`Unknown pallet rack cell: ${cellId}`);
    }
    return cell;
  }

  private validateCargoPlacement(placement: PalletRackCargoPlacement): PalletRackCargoPlacement {
    const cell = this.getCell(placement.cellId);
    validateIndex(placement.cellIndex, this.cells.length, "cellIndex");
    if (this.cells[placement.cellIndex]?.id !== placement.cellId) {
      throw new RangeError("cellIndex does not match cellId");
    }
    validateIndex(placement.heightIndex, this.layerHeights.length, "heightIndex");
    validateIndex(placement.slotIndex, cell.cargoCapacity, "slotIndex");
    return { ...placement };
  }

  private isHitTarget(object: Object3D): object is PalletRackHitTarget {
    return object.userData.palletRack === this && object.userData.palletRackCargoCell !== undefined;
  }
}

function createCellFrames(
  cells: DxfPalletRackCellPlacement[],
  postWidth: number,
  postDepth: number
) {
  const frames = new Map<string, PalletRackCellFrame>();

  cells.forEach((cell, index) => {
    const previous = cells[index - 1];
    const next = cells[index + 1];
    const previousOverlap = previous
      ? Math.max(
          0,
          Math.min(previous.positionX + previous.length, cell.positionX + cell.length) -
            Math.max(previous.positionX, cell.positionX)
        )
      : 0;
    const nextOverlap = next
      ? Math.max(
          0,
          Math.min(next.positionX + next.length, cell.positionX + cell.length) -
            Math.max(next.positionX, cell.positionX)
        )
      : 0;
    const leftInset =
      previousOverlap > layoutTolerance
        ? previousOverlap / 2
        : Math.max(nextOverlap / 2, postWidth / 2);
    const rightInset =
      nextOverlap > layoutTolerance
        ? nextOverlap / 2
        : Math.max(previousOverlap / 2, postWidth / 2);
    const frontInset = Math.min(postDepth / 2, cell.width / 4);

    frames.set(cell.id, {
      backZ: cell.positionZ + cell.width - frontInset,
      cell,
      frontZ: cell.positionZ + frontInset,
      leftX: cell.positionX + leftInset,
      rightX: cell.positionX + cell.length - rightInset
    });
  });

  return frames;
}

function createPostPositions(frames: Map<string, PalletRackCellFrame>) {
  const positions = new Map<
    string,
    { direction: RackPostDirection; position: { x: number; z: number } }
  >();

  frames.forEach((frame) => {
    [frame.leftX, frame.rightX].forEach((x) => {
      [
        { direction: RackPostDirection.Front, z: frame.frontZ },
        { direction: RackPostDirection.Back, z: frame.backZ }
      ].forEach(({ direction, z }) => {
        const key = `${roundLayoutValue(x)}:${roundLayoutValue(z)}`;
        if (!positions.has(key)) {
          positions.set(key, { direction, position: { x, z } });
        }
      });
    });
  });

  return [...positions.values()];
}

function createSideFrames(frames: Map<string, PalletRackCellFrame>) {
  const sideFrames = new Map<string, PalletRackSideFrame>();
  frames.forEach((frame) => {
    [frame.leftX, frame.rightX].forEach((x) => {
      const key = `${roundLayoutValue(x)}:${roundLayoutValue(frame.frontZ)}:${roundLayoutValue(frame.backZ)}`;
      if (!sideFrames.has(key)) {
        sideFrames.set(key, { backZ: frame.backZ, frontZ: frame.frontZ, x });
      }
    });
  });
  return [...sideFrames.values()];
}

async function createSideBraceFrame(
  frame: PalletRackSideFrame,
  index: number,
  totalHeight: number,
  rack: PalletRack,
  loader: ModelAssetLoader
) {
  const width = frame.backZ - frame.frontZ;
  const topBraceY = totalHeight - topBraceInset;
  const layout = createDiagonalBraceLayout(bottomBraceY, topBraceY, width);
  const [bottomBrace, topBrace, ...diagonalBraces] = await Promise.all([
    RackShelfSideBrace.create(loader),
    RackShelfSideBrace.create(loader),
    ...Array.from({ length: layout?.count ?? 0 }, () => RackShelfSideBrace.create(loader))
  ]);
  const clipPlanes = Array.from({ length: 6 }, () => new Plane());
  const root = new Group();
  root.name = `pallet-rack-side-brace-${index}`;
  root.userData.palletRackSideBrace = { clipPlanes, frame, totalHeight };
  root.position.set(frame.x + columnBraceXOffset, 0, frame.frontZ);
  root.add(bottomBrace, topBrace, ...diagonalBraces);

  [bottomBrace, topBrace, ...diagonalBraces].forEach((brace) => {
    brace.setClippingPlanes(clipPlanes);
  });
  updateHorizontalBrace(bottomBrace, bottomBraceY);
  updateHorizontalBrace(topBrace, topBraceY);

  diagonalBraces.forEach((brace, braceIndex) => {
    if (!layout) {
      brace.visible = false;
      return;
    }
    const isForward = braceIndex % 2 === 0;
    brace.position.set(0, bottomBraceY + layout.segmentHeight * braceIndex, isForward ? 0 : width);
    brace.rotation.set(isForward ? -layout.angle : Math.PI + layout.angle, 0, 0);
    brace.setLengthScale(columnBraceLengthScale / Math.cos(layout.angle));
  });

  rack.updateWorldMatrix(true, true);
  return root;
}

function updateHorizontalBrace(brace: RackShelfSideBrace, y: number) {
  brace.position.set(0, y, 0);
  brace.rotation.set(0, 0, 0);
  brace.setLengthScale(columnBraceLengthScale);
}

function updateSideBraceClipPlanes(structure: Group, rack: PalletRack) {
  rack.updateWorldMatrix(true, true);
  structure.traverse((object) => {
    const data = object.userData.palletRackSideBrace as
      | { clipPlanes: Plane[]; frame: PalletRackSideFrame; totalHeight: number }
      | undefined;
    if (!data) {
      return;
    }

    const sideX = data.frame.x + columnBraceXOffset;
    const points = [
      { normal: new Vector3(1, 0, 0), point: new Vector3(sideX - columnClipAreaXHalfLength, 0, 0) },
      {
        normal: new Vector3(-1, 0, 0),
        point: new Vector3(sideX + columnClipAreaXHalfLength, 0, 0)
      },
      { normal: new Vector3(0, 1, 0), point: new Vector3(0, 0, 0) },
      { normal: new Vector3(0, -1, 0), point: new Vector3(0, data.totalHeight, 0) },
      { normal: new Vector3(0, 0, 1), point: new Vector3(0, 0, data.frame.frontZ) },
      { normal: new Vector3(0, 0, -1), point: new Vector3(0, 0, data.frame.backZ) }
    ];

    points.forEach(({ normal, point }, index) => {
      const worldNormal = normal.transformDirection(rack.matrixWorld);
      const worldPoint = point.applyMatrix4(rack.matrixWorld);
      data.clipPlanes[index].setFromNormalAndCoplanarPoint(worldNormal, worldPoint);
    });
  });
}

function createDiagonalBraceLayout(bottomY: number, topY: number, width: number) {
  const height = topY - bottomY;
  if (height <= 0 || width <= 0) {
    return undefined;
  }

  const minCount = Math.max(1, Math.ceil(height / (Math.tan(diagonalBraceMaxAngle) * width)));
  const maxCount = Math.floor(height / (Math.tan(diagonalBraceMinAngle) * width));
  if (maxCount < minCount) {
    return undefined;
  }

  const count = maxCount;
  const segmentHeight = height / count;
  return { angle: Math.atan2(segmentHeight, width), count, segmentHeight };
}

function createHitTarget(rack: PalletRack, cell: PalletRackCargoCell) {
  const material = new MeshBasicMaterial({ depthWrite: false, opacity: 0, transparent: true });
  material.colorWrite = false;
  const target = new Mesh(new BoxGeometry(1, 1, 1), material) as PalletRackHitTarget;
  target.name = `pallet-rack-cell-${cell.cellIndex}-${cell.heightIndex}`;
  target.userData.palletRack = rack;
  target.userData.palletRackCargoCell = cell;
  target.visible = false;
  return target;
}

function getRackBounds(cells: DxfPalletRackCellPlacement[]) {
  return {
    maxX: Math.max(...cells.map((cell) => cell.positionX + cell.length)),
    maxZ: Math.max(...cells.map((cell) => cell.positionZ + cell.width)),
    minX: Math.min(...cells.map((cell) => cell.positionX)),
    minZ: Math.min(...cells.map((cell) => cell.positionZ))
  };
}

function createCargoRoot(object: Object3D) {
  const root = new Group();
  root.add(object);
  root.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(object);
  const center = bounds.getCenter(new Vector3());
  object.position.x -= center.x;
  object.position.y -= bounds.min.y;
  object.position.z -= center.z;
  return root;
}

function createCargoPlacementKey(placement: PalletRackCargoPlacement) {
  return `${placement.cellId}:${placement.heightIndex}:${placement.slotIndex}`;
}

function validateCells(cells: DxfPalletRackCellPlacement[]) {
  if (cells.length === 0) {
    throw new RangeError("PalletRack requires at least one cell");
  }
  cells.forEach((cell) => {
    validatePositiveNumber(cell.length, "cell.length");
    validatePositiveNumber(cell.width, "cell.width");
    validatePositiveInteger(cell.cargoCapacity, "cell.cargoCapacity");
  });
  return cells;
}

function validateLayerHeights(layerHeights: number[]) {
  if (layerHeights.length === 0) {
    throw new RangeError("layerHeights requires at least one layer");
  }
  layerHeights.forEach((height) => {
    validatePositiveNumber(height, "layerHeight");
  });
  return [...layerHeights];
}

function validatePositiveNumber(value: number, name: string) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive finite number`);
  }
  return value;
}

function validatePositiveInteger(value: number, name: string) {
  if (!Number.isInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive integer`);
  }
  return value;
}

function validateIndex(value: number, count: number, name: string) {
  if (!Number.isInteger(value) || value < 0 || value >= count) {
    throw new RangeError(`${name} must be an integer between 0 and ${count - 1}`);
  }
}

function roundLayoutValue(value: number) {
  return Math.round(value / layoutTolerance) * layoutTolerance;
}

function areEqualNumbers(left: number[], right: number[]) {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}
